import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import OrdersTable from '../components/order/OrdersTable';
import useOrderList from '../hooks/reactQuery/useOrderList';
import KpiStrip from '../components/KpiStrip';
import useListPage, { initialParams } from '../hooks/useListPage';
import { usePublishPageMeta } from '../context/PageMeta';
import useElementHeight from '../hooks/useElementHeight';

interface IProps {
  section: string;
  showKpis?: boolean;
  emptyMessage?: string;
  handleModalOpen: (data: any) => void;
  rowDetails: any;
  setRowDetails: any;
  orderCounts?: { [key: string]: number };
}

const listFromUrl = (params: URLSearchParams, key: string) =>
  params.get(key)?.split('|').filter(Boolean) ?? [];

// One order list page: per-column filters, paging and the order pop-up, scoped to an API
// section ('Search' for everything, or a pipeline stage such as 'New Orders'). Filters start
// from the URL so a shared link lands on the same view.
const OrderListPage = ({
  section,
  showKpis = false,
  emptyMessage,
  handleModalOpen,
  rowDetails,
  setRowDetails,
  orderCounts,
}: IProps) => {
  const [params] = useState(initialParams);
  const [customerName, setCustomerName] = useState(
    params.get('customerName') ?? '',
  );
  const [pid, setPid] = useState(params.get('pid') ?? '');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [phone, setPhone] = useState(params.get('phone') ?? '');
  const [statuses, setStatuses] = useState<string[]>(() =>
    listFromUrl(params, 'status'),
  );
  const [kitters, setKitters] = useState<string[]>(() =>
    listFromUrl(params, 'kitter'),
  );
  const [sources, setSources] = useState<string[]>(() =>
    listFromUrl(params, 'source'),
  );
  const [selectedDate, setSelectedDate] = useState<any>(
    params.get('orderDate') ?? null,
  );

  // Rows per page come from what fits under the KPI board, whose height depends on the
  // viewport width (its head wraps and its tiles grow on narrow desktops).
  const [kpiRef, kpiHeight] = useElementHeight<HTMLDivElement>(144);

  const filter = {
    customerName,
    pid,
    status: statuses.length ? statuses.join('|') : undefined,
    kitter: kitters.length ? kitters.join('|') : undefined,
    source: sources.length ? sources.join('|') : undefined,
    // Calendar days; the API turns them into a Toronto-time range that covers both ends.
    orderDate: selectedDate,
    // The list selects EmailAddress and PhoneNumber; filters are matched by column name.
    emailAddress: email,
    phoneNumber: phone,
    section,
  };
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    onPageSizeChange,
    isAuto,
    fitSize,
  } = useListPage(
    270 + (showKpis ? kpiHeight + 16 : 0),
    JSON.stringify(filter),
  );

  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useOrderList({
    limit: pageSize,
    page: currentPage,
    filter,
  });
  usePublishPageMeta({ total: data?.total, dataUpdatedAt, refetch });

  return (
    <div className="main-content">
      {showKpis ? (
        <div ref={kpiRef}>
          <KpiStrip counts={orderCounts} />
        </div>
      ) : null}
      {isLoading ? (
        <div className="spinner-position">
          <Spinner />
        </div>
      ) : (
        <OrdersTable
          data={data}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          handleModalOpen={handleModalOpen}
          itemsPerPage={pageSize}
          onPageSizeChange={onPageSizeChange}
          isAutoPageSize={isAuto}
          fitPageSize={fitSize}
          isFetching={isFetching}
          setCustomerName={setCustomerName}
          searchCustomerName={customerName}
          pid={pid}
          setPid={setPid}
          selectedKitters={kitters}
          setKitters={setKitters}
          selectedSources={sources}
          setSources={setSources}
          setStatuses={setStatuses}
          selectedStatuses={statuses}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          rowDetails={rowDetails}
          setRowDetails={setRowDetails}
          email={email}
          setEmail={setEmail}
          phone={phone}
          setPhone={setPhone}
          emptyMessage={emptyMessage}
        />
      )}
    </div>
  );
};

export default OrderListPage;
